import { useEffect, useMemo, useRef, useState } from "react";
import { fetchPrintJob } from "../api/printJobs";
import { generateBarcodeDataUrl } from "../utils/barcodes";

const parsePrintParams = () => {
  try {
    const url = new URL(window.location.href);
    return {
      jobId: url.searchParams.get("jobId") || "",
      token: url.searchParams.get("token") || "",
    };
  } catch (_err) {
    return { jobId: "", token: "" };
  }
};

const getLabelSize = (layout = {}) => {
  const widthIn = Number(layout?.labelWidthIn);
  const heightIn = Number(layout?.labelHeightIn);
  return {
    widthIn: Number.isFinite(widthIn) && widthIn > 0 ? widthIn : 2,
    heightIn: Number.isFinite(heightIn) && heightIn > 0 ? heightIn : 1,
  };
};

const buildLabelKey = (label, index) =>
  label?.instanceId || label?._instance || label?.id || label?.barcode || `label-${index}`;

const preloadImage = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });

function PrintBarcodesPage() {
  const [{ jobId, token }] = useState(() => parsePrintParams());
  const [labels, setLabels] = useState([]);
  const [layout, setLayout] = useState({});
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const hasPrinted = useRef(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!jobId || !token) {
        setError("Missing print job details.");
        setStatus("error");
        return;
      }

      try {
        const job = await fetchPrintJob(jobId, token);
        const jobLabels = Array.isArray(job?.labels) ? job.labels : [];
        if (!jobLabels.length) {
          throw new Error("No labels available for this print job.");
        }

        const imageCache = new Map();
        const rendered = await Promise.all(
          jobLabels.map(async (label, index) => {
            const value = `${label?.barcode ?? ""}`.trim();
            let image = "";
            if (value) {
              if (!imageCache.has(value)) {
                const dataUrl = await generateBarcodeDataUrl(value, {
                  height: 48,
                  margin: 0,
                  width: 1.5,
                });
                await preloadImage(dataUrl);
                imageCache.set(value, dataUrl);
              }
              image = imageCache.get(value);
            }
            return { ...label, image, _key: buildLabelKey(label, index) };
          })
        );

        if (!active) return;
        setLayout(job?.layout || {});
        setLabels(rendered);
        setStatus("ready");
      } catch (err) {
        if (!active) return;
        setError(err?.message || "Unable to load print job.");
        setStatus("error");
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [jobId, token]);

  useEffect(() => {
    if (status !== "ready" || hasPrinted.current) return;
    hasPrinted.current = true;

    const id = window.setTimeout(() => {
      window.print();
    }, 250);

    return () => window.clearTimeout(id);
  }, [status]);

  useEffect(() => {
    const handleAfterPrint = () => {
      if (window.opener) {
        window.close();
      }
    };

    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, []);

  const { widthIn, heightIn } = useMemo(() => getLabelSize(layout), [layout]);
  const pageStyle = useMemo(
    () => `@page { size: ${widthIn}in ${heightIn}in; margin: 0; }`,
    [widthIn, heightIn]
  );

  return (
    <div className="print-page" style={{ "--label-width": `${widthIn}in`, "--label-height": `${heightIn}in` }}>
      <style>{pageStyle}</style>
      <header className="print-toolbar">
        <div>
          <p className="muted extra-small">Barcode labels</p>
          <h2>Print labels</h2>
        </div>
        <div className="print-toolbar__actions">
          <button className="ghost-btn" type="button" onClick={() => window.print()}>
            Print
          </button>
          <button className="primary-btn" type="button" onClick={() => window.close()}>
            Close
          </button>
        </div>
      </header>

      {status === "loading" ? (
        <div className="print-status">Preparing labels…</div>
      ) : null}

      {status === "error" ? (
        <div className="print-status error">
          <div className="inline-alert error compact">{error || "Unable to load labels."}</div>
        </div>
      ) : null}

      {status === "ready" ? (
        <div className="print-grid">
          {labels.map((label, index) => (
            <div className="barcode-label print-label" key={label._key || index}>
              <div className="barcode-label__meta">
                <div className="barcode-label__name">{label.name || "Untitled product"}</div>
              </div>
              <div className="barcode-label__image">
                {label.image ? <img src={label.image} alt={`Barcode ${label.barcode || ""}`} /> : null}
              </div>
              <div className="barcode-label__code">{label.barcode || ""}</div>
              <div className="barcode-label__footer" />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default PrintBarcodesPage;
